import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  FaChevronLeft,
  FaChevronRight,
  FaList,
  FaRedo,
  FaSearch,
  FaTh,
  FaTimes,
} from "react-icons/fa";
import { LuChevronRight, LuFactory, LuFileText, LuGavel, LuUsers } from "react-icons/lu";
import HubHomeFooter from "../hub/HubHomeFooter";
import MarketplaceAdSection from "./MarketplaceAdSection";
import AuctionListingGridCard from "./AuctionListingGridCard";
import AuctionListingListRow from "./AuctionListingListRow";
import { FilterSelect } from "./MarketplaceBrowseUi";
import { buildMarketplaceAuctionBrowseHref } from "../../constants/auctionBrowse";
import { buildMarketplaceModuleTabs } from "../../constants/marketplaceModuleTabs";
import { goToBuyerRfqPost } from "../../utils/buyerRfqRedirect";
import { useShowListAsSupplierButton } from "../../hooks/useShowListAsSupplierButton";
import "../../assets/styles/explore_product_categories.scss";
import "../../assets/styles/product_marketplace.scss";

const AUCTION_SORT_OPTIONS = [
  { value: "ending_soon", label: "Ending Soon" },
  { value: "newest", label: "Newest First" },
  { value: "price_low", label: "Price: Low to High" },
  { value: "price_high", label: "Price: High to Low" },
];

function formatStatCount(value) {
  return (Number(value) || 0).toLocaleString();
}

function auctionSubcategoryDescription(name) {
  return `Browse and bid on live ${name} auctions. Compare starting prices, current bids, and seller details from verified auction listings.`;
}

function AuctionHeroStats({ subCategory, total }) {
  const auctions = total || subCategory?.auction_count || 0;
  const sellers = subCategory?.supplier_count || subCategory?.seller_count || 0;
  const buyerReqs = subCategory?.buyer_request_count || 0;
  const products = subCategory?.product_count || 0;

  const pills = [
    { value: auctions, label: "live auctions", icon: LuGavel, iconClass: "explore-subcat-products-hero__stat-icon--auctions" },
    { value: sellers, label: "active sellers", icon: LuUsers, iconClass: "explore-subcat-products-hero__stat-icon--suppliers" },
    { value: buyerReqs, label: "buyer requirements", icon: LuFileText, iconClass: "explore-subcat-products-hero__stat-icon--buyer-reqs" },
    { value: products, label: "related products", icon: LuFactory, iconClass: "explore-subcat-products-hero__stat-icon--cm" },
  ];

  return (
    <div className="explore-subcat-products-hero__stats">
      {pills.map((pill) => {
        const Icon = pill.icon;
        return (
          <div key={pill.label} className="explore-subcat-products-hero__stat-pill">
            <div className={`explore-subcat-products-hero__stat-icon ${pill.iconClass}`}>
              <Icon aria-hidden />
            </div>
            <span className="explore-subcat-products-hero__stat-text">
              <strong>{formatStatCount(pill.value)}</strong> {pill.label}
            </span>
          </div>
        );
      })}
    </div>
  );
}

export default function ExploreAuctionListingsBrowse({
  parentCategory,
  subCategory,
  auctions = [],
  loading = false,
  applied,
  draft,
  setDraft,
  filteredTotal = 0,
  filterOptions = {},
  categoryOptions = [],
  subCategoryOptions = [],
  onSearchSubmit,
  onApplyFilters,
  onClearFilters,
  onSortChange,
  onGoToPage,
  onChangePageSize,
  pageSize = 12,
  pageNumbers = [],
  totalPages = 0,
  advertisementBanners = [],
  compareIds = [],
  onToggleCompare,
}) {
  const navigate = useNavigate();
  const showListAsSupplier = useShowListAsSupplierButton();
  const [localSearch, setLocalSearch] = useState(applied.q || "");
  const [viewMode, setViewMode] = useState("list");

  useEffect(() => {
    setLocalSearch(applied.q || "");
  }, [applied.q]);

  const trimmedSearch = String(applied.q || "").trim();
  const isSearchMode = !subCategory && Boolean(trimmedSearch);
  const subCategoryName = subCategory?.name || subCategory?.sub_category_name || "Auctions";
  const parentName = parentCategory?.name || parentCategory?.category_name || "";
  const parentCategoryId = parentCategory?.id ?? parentCategory?.category_id;
  const pageTitle = isSearchMode ? trimmedSearch : subCategoryName;
  const pageDescription = isSearchMode
    ? `Browse live auctions matching "${trimmedSearch}". Place bids on verified listings across categories.`
    : auctionSubcategoryDescription(subCategoryName);
  const showingTotal = filteredTotal || auctions.length || 0;
  const moduleTabs = useMemo(
    () =>
      buildMarketplaceModuleTabs({
        activeKey: "auctions",
        categoryId: parentCategoryId,
        subCategoryId: subCategory?.id ?? subCategory?.sub_category_id,
        counts: {
          products: subCategory?.product_count || 0,
          buyerReqs: subCategory?.buyer_request_count || 0,
          auctions: showingTotal || subCategory?.auction_count || 0,
          cm: subCategory?.cm_count || 0,
        },
      }),
    [parentCategoryId, subCategory, showingTotal],
  );
  const categoryBrowseHref = buildMarketplaceAuctionBrowseHref({
    auctionCategory: parentCategoryId,
  });

  return (
    <>
      <section className="explore-subcat-products-hero explore-subcat-products-hero--auction">
        <div className="explore-subcat-products-hero__inner">
          <button
            type="button"
            className="explore-subcat-products-hero__back"
            onClick={() => navigate(categoryBrowseHref)}
          >
            <FaChevronLeft aria-hidden /> Back
          </button>
          <nav className="explore-subcat-products-hero__breadcrumb" aria-label="Breadcrumb">
            <Link to="/home">Home</Link>
            <LuChevronRight className="explore-subcat-products-hero__breadcrumb-sep" aria-hidden />
            <Link to="/marketplace/products?module=auctions">Auctions</Link>
            {parentName ? (
              <>
                <LuChevronRight className="explore-subcat-products-hero__breadcrumb-sep" aria-hidden />
                <Link to={categoryBrowseHref}>{parentName}</Link>
              </>
            ) : null}
            {isSearchMode ? (
              <>
                <LuChevronRight className="explore-subcat-products-hero__breadcrumb-sep" aria-hidden />
                <span className="is-current">Search: {trimmedSearch}</span>
              </>
            ) : subCategoryName ? (
              <>
                <LuChevronRight className="explore-subcat-products-hero__breadcrumb-sep" aria-hidden />
                <span className="is-current">{subCategoryName}</span>
              </>
            ) : null}
          </nav>

          <div className="explore-subcat-products-hero__main">
            <div className="explore-subcat-products-hero__intro">
              <h1>{pageTitle}</h1>
              <p>{pageDescription}</p>
            </div>
            <AuctionHeroStats subCategory={subCategory} total={showingTotal} />
          </div>
        </div>
      </section>

      <div className="explore-subcat-products-tabs-bar">
        <div className="explore-subcat-products-tabs-bar__inner">
          <nav className="explore-subcat-products-tabs" aria-label="Listing types">
            {moduleTabs.map((tab) =>
              tab.active ? (
                <span key={tab.key} className="explore-subcat-products-tabs__item is-active">
                  {tab.label}
                </span>
              ) : (
                <Link key={tab.key} to={tab.href} className="explore-subcat-products-tabs__item">
                  {tab.label}
                </Link>
              ),
            )}
          </nav>
        </div>
      </div>

      <section className="explore-subcat-products-controls">
        <div className="explore-subcat-products-controls__inner">
          <div className="explore-subcat-products-controls__top-row">
            <form
              className="explore-subcat-products-controls__search"
              onSubmit={(e) => {
                e.preventDefault();
                onSearchSubmit?.(localSearch);
              }}
            >
              <FaSearch aria-hidden />
              <input
                type="search"
                value={localSearch}
                onChange={(e) => setLocalSearch(e.target.value)}
                placeholder={
                  isSearchMode
                    ? "Refine auction search — product, seller, location…"
                    : `Search ${subCategoryName.toLowerCase()} auctions, seller, location…`
                }
                aria-label={isSearchMode ? "Search auctions" : `Search ${subCategoryName} auctions`}
              />
              {localSearch ? (
                <button
                  type="button"
                  className="explore-subcat-products-controls__clear"
                  aria-label="Clear search"
                  onClick={() => {
                    setLocalSearch("");
                    onSearchSubmit?.("");
                  }}
                >
                  <FaTimes aria-hidden />
                </button>
              ) : null}
            </form>

            <div className="explore-subcat-products-controls__sort" role="group" aria-label="Sort auctions">
              {AUCTION_SORT_OPTIONS.map((opt) => {
                const effectiveSort = applied.sort || "ending_soon";
                const isActive = effectiveSort === opt.value;
                return (
                  <button
                    key={opt.value}
                    type="button"
                    className={`explore-subcat-products-controls__sort-pill${isActive ? " is-active" : ""}`}
                    aria-pressed={isActive}
                    onClick={() => onSortChange?.(opt.value)}
                  >
                    {opt.label}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="explore-subcat-products-controls__actions">
            <button
              type="button"
              className="explore-subcat-products-controls__btn explore-subcat-products-controls__btn--orange"
              onClick={() => goToBuyerRfqPost(navigate)}
            >
              Post Buying Requirement
            </button>
            {showListAsSupplier ? (
              <button
                type="button"
                className="explore-subcat-products-controls__btn explore-subcat-products-controls__btn--navy"
                onClick={() => navigate("/auction/create")}
              >
                List Auction
              </button>
            ) : null}
          </div>
        </div>
      </section>

      <div className="explore-subcat-products-main">
        <aside className="product-marketplace-filters">
          <div className="product-marketplace-filters__head">
            <h2>Filters</h2>
            <button type="button" onClick={onClearFilters}>
              Clear All
            </button>
          </div>

          <FilterSelect
            id="au-category"
            label="Category"
            value={draft.category}
            placeholder="All Categories"
            options={categoryOptions}
            onChange={(e) => setDraft((d) => ({ ...d, category: e.target.value, subCategory: "" }))}
            onClear={() => setDraft((d) => ({ ...d, category: "", subCategory: "" }))}
          />

          <FilterSelect
            id="au-subcategory"
            label="Sub Category"
            value={draft.subCategory}
            disabled={!draft.category}
            placeholder="All Sub Categories"
            options={subCategoryOptions}
            onChange={(e) => setDraft((d) => ({ ...d, subCategory: e.target.value }))}
            onClear={() => setDraft((d) => ({ ...d, subCategory: "" }))}
          />

          <div className="product-marketplace-filters__field">
            <label>Starting Price Range</label>
            <div className="product-marketplace-filters__price-row">
              <div className="product-marketplace-filters__price-field">
                <span>₹</span>
                <input
                  type="number"
                  placeholder="Min Price"
                  value={draft.minPrice}
                  onChange={(e) => setDraft((d) => ({ ...d, minPrice: e.target.value }))}
                />
              </div>
              <div className="product-marketplace-filters__price-field">
                <span>₹</span>
                <input
                  type="number"
                  placeholder="Max Price"
                  value={draft.maxPrice}
                  onChange={(e) => setDraft((d) => ({ ...d, maxPrice: e.target.value }))}
                />
              </div>
            </div>
          </div>

          <details className="product-marketplace-filters__accordion" open>
            <summary>Brand</summary>
            <select value={draft.brand} onChange={(e) => setDraft((d) => ({ ...d, brand: e.target.value }))}>
              <option value="">All Brands</option>
              {(filterOptions.brands || []).map((brand) => (
                <option key={brand} value={brand}>
                  {brand}
                </option>
              ))}
            </select>
          </details>

          <details className="product-marketplace-filters__accordion">
            <summary>Country</summary>
            <select value={draft.country} onChange={(e) => setDraft((d) => ({ ...d, country: e.target.value }))}>
              <option value="">All Countries</option>
              {(filterOptions.countries || []).map((country) => (
                <option key={country} value={country}>
                  {country}
                </option>
              ))}
            </select>
          </details>

          <details className="product-marketplace-filters__accordion">
            <summary>Condition</summary>
            <select value={draft.condition} onChange={(e) => setDraft((d) => ({ ...d, condition: e.target.value }))}>
              <option value="">All Conditions</option>
              {(filterOptions.conditions || []).map((condition) => (
                <option key={condition} value={condition}>
                  {condition}
                </option>
              ))}
            </select>
          </details>

          <details className="product-marketplace-filters__accordion">
            <summary>End Condition</summary>
            <select
              value={draft.endCondition}
              onChange={(e) => setDraft((d) => ({ ...d, endCondition: e.target.value }))}
            >
              <option value="">All end conditions</option>
              <option value="End on Scheduled Date Only">End on scheduled date only</option>
              <option value="End When Reserve Met">End when reserve met</option>
            </select>
          </details>

          <div className="product-marketplace-filters__actions">
            <button
              type="button"
              className="product-marketplace-btn product-marketplace-btn--primary"
              onClick={onApplyFilters}
            >
              Apply Filters
            </button>
            <button
              type="button"
              className="product-marketplace-btn product-marketplace-btn--outline"
              onClick={onClearFilters}
            >
              <FaRedo aria-hidden /> Reset Filters
            </button>
          </div>
        </aside>

        <section className="explore-subcat-products-content">
          <div className="explore-subcat-products-content__head">
            <p className="explore-subcat-products-content__count">
              {showingTotal > 0
                ? isSearchMode
                  ? `${showingTotal.toLocaleString()} result${showingTotal === 1 ? "" : "s"} for "${trimmedSearch}" — Auctions`
                  : `${showingTotal.toLocaleString()} result${showingTotal === 1 ? "" : "s"} in ${subCategoryName} — Auctions`
                : isSearchMode
                  ? `0 results for "${trimmedSearch}" — Auctions`
                  : `0 results in ${subCategoryName} — Auctions`}
            </p>
            <div className="explore-subcat-products-view-toggle" aria-label="View mode">
              <button
                type="button"
                className={`explore-subcat-products-view-toggle__btn${viewMode === "list" ? " is-active" : ""}`}
                onClick={() => setViewMode("list")}
                aria-pressed={viewMode === "list"}
                aria-label="List view"
              >
                <FaList aria-hidden />
              </button>
              <button
                type="button"
                className={`explore-subcat-products-view-toggle__btn${viewMode === "grid" ? " is-active" : ""}`}
                onClick={() => setViewMode("grid")}
                aria-pressed={viewMode === "grid"}
                aria-label="Grid view"
              >
                <FaTh aria-hidden />
              </button>
            </div>
          </div>

          <div className={`explore-subcat-products-list explore-subcat-products-list--${viewMode}`}>
            {loading && auctions.length === 0
              ? Array.from({ length: Math.min(pageSize, viewMode === "grid" ? 8 : 6) }).map((_, idx) => (
                  <div
                    key={idx}
                    className={
                      viewMode === "grid"
                        ? "marketplace-product-grid-card marketplace-product-grid-card--skeleton"
                        : "marketplace-product-row marketplace-product-row--skeleton"
                    }
                    aria-hidden
                  />
                ))
              : null}
            {!loading || auctions.length
              ? auctions.map((auction) =>
                  viewMode === "grid" ? (
                    <AuctionListingGridCard
                      key={auction.auction_id}
                      auction={auction}
                      onToggleCompare={onToggleCompare}
                      compared={compareIds.includes(Number(auction.auction_id))}
                    />
                  ) : (
                    <AuctionListingListRow
                      key={auction.auction_id}
                      auction={auction}
                      onToggleCompare={onToggleCompare}
                      compared={compareIds.includes(Number(auction.auction_id))}
                    />
                  ),
                )
              : null}
            {!loading && auctions.length === 0 ? (
              <p className="explore-categories-empty">No auctions match your filters yet.</p>
            ) : null}
          </div>

          {totalPages > 0 ? (
            <div className="product-marketplace-pagination-wrap">
              <nav className="product-marketplace-pagination" aria-label="Pagination">
                <button
                  type="button"
                  disabled={Number(applied.page) <= 1}
                  onClick={() => onGoToPage?.(Number(applied.page) - 1)}
                >
                  <FaChevronLeft aria-hidden /> Previous
                </button>
                {pageNumbers.map((page, idx) =>
                  page === "…" ? (
                    <span key={`ellipsis-${idx}`} className="product-marketplace-pagination__ellipsis">
                      …
                    </span>
                  ) : (
                    <button
                      key={page}
                      type="button"
                      className={Number(applied.page) === page ? "is-active" : ""}
                      onClick={() => onGoToPage?.(page)}
                    >
                      {page}
                    </button>
                  ),
                )}
                <button
                  type="button"
                  disabled={Number(applied.page) >= totalPages}
                  onClick={() => onGoToPage?.(Number(applied.page) + 1)}
                >
                  Next <FaChevronRight aria-hidden />
                </button>
              </nav>
              <label className="product-marketplace-page-size">
                Show
                <select value={pageSize} onChange={(e) => onChangePageSize?.(e.target.value)} aria-label="Auctions per page">
                  {[12, 24, 36, 48].map((size) => (
                    <option key={size} value={size}>
                      {size}
                    </option>
                  ))}
                </select>
                per page
              </label>
            </div>
          ) : null}
        </section>
      </div>

      {advertisementBanners?.length ? <MarketplaceAdSection banners={advertisementBanners} /> : null}
      <HubHomeFooter />
    </>
  );
}
